import { useCallback, useEffect, useRef, useState } from "react";
import { uploadUrl } from "../lib/api";
import type { RoomObject } from "../lib/useRoomChannel";
import type { Tile } from "../lib/useLiveRoom";

// 16:9, matching the shape of almost every screen this runs on. A 3:2 space
// had to be letterboxed into a widescreen viewport, which is where the dead
// black margin came from — and anything dragged into that margin was outside
// the room and unreachable.
export const CANVAS_W = 1920;
export const CANVAS_H = 1080;

export const STROKE_COLORS: Record<string, string> = {
  chalk: "#f4f1ea",
  ink: "#1b1b1b",
  red: "#d9534f",
  amber: "#e0a33e",
  green: "#5c9e5f",
  blue: "#4a7fb5",
};

type DragState = {
  id: string;
  mode: "move" | "resize";
  startX: number;
  startY: number;
  origin: { x: number; y: number; width: number; height: number };
};

type Point = [number, number];

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

type Props = {
  objects: RoomObject[];
  tilesByIdentity: Map<string, Tile>;
  nameByUserId: Map<number, string>;
  myUserId: number;
  selectedId: string | null;
  drawMode: boolean;
  drawColor: string;
  drawWidth: number;
  onSelect: (id: string | null) => void;
  onMove: (patch: Partial<RoomObject> & { id: string }) => void;
  onPlace: (patch: Partial<RoomObject> & { id: string }) => void;
  onDrawn: (strokes: Point[][], bounds: { x: number; y: number; w: number; h: number }) => void;
  /** A point to bring into view, in canvas units. */
  focusOn?: { x: number; y: number } | null;
};

/**
 * The room.
 *
 * Everything is an object at a position: camera frames, screens, photos,
 * notes, scribbles. The app never arranges anyone — it draws the arrangement
 * the room remembers, and anybody in the room can change it by dragging.
 */
export function Canvas({
  objects,
  tilesByIdentity,
  nameByUserId,
  myUserId,
  selectedId,
  drawMode,
  drawColor,
  drawWidth,
  onSelect,
  onMove,
  onPlace,
  onDrawn,
  focusOn,
}: Props) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  // What the view is *of*, in canvas units, rather than where the canvas sits
  // in pixels. Everything the eye cares about — resizing the window, turning a
  // phone, panning — is a statement about which part of the room is being
  // looked at, and expressing it that way keeps that point fixed for free
  // across changes of scale.
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [centre, setCentre] = useState({ cx: CANVAS_W / 2, cy: CANVAS_H / 2 });
  const dragRef = useRef<DragState | null>(null);
  const panRef = useRef<{ x: number; y: number; cx: number; cy: number } | null>(null);
  const lastSent = useRef(0);

  // In-progress scribble, kept local until the pen lifts.
  const [strokes, setStrokes] = useState<Point[][]>([]);
  const drawingRef = useRef(false);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      setSize({ w: width, h: height });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Cover, not contain. Contain would fit the whole 16:9 room into a portrait
  // phone by shrinking it to a strip a few centimetres tall — legible to
  // nobody. Cover keeps faces at a usable size and lets the rest be panned to,
  // which is why panning below is not a nicety on small screens.
  const scale = size.w && size.h ? Math.max(size.w / CANVAS_W, size.h / CANVAS_H) : 1;

  // Clamped so an edge of the room can never come inside the viewport: past
  // the ends of these ranges the app would be showing its own background.
  const view = {
    scale,
    x: clamp(size.w / 2 - centre.cx * scale, size.w - CANVAS_W * scale, 0),
    y: clamp(size.h / 2 - centre.cy * scale, size.h - CANVAS_H * scale, 0),
  };

  // Bring a point into view — used to put the newcomer's own camera frame in
  // front of them. On a wide screen the clamp usually makes this a no-op; on a
  // phone, where the crop discards most of the width, it is the difference
  // between seeing yourself and staring at an empty corner of the room.
  //
  // Once only. Re-centring whenever the point moved would wrench the view back
  // every time someone dragged their own frame.
  const focused = useRef(false);
  useEffect(() => {
    if (!focusOn || focused.current) return;
    focused.current = true;
    setCentre({ cx: focusOn.x, cy: focusOn.y });
  }, [focusOn]);

  // Screen pixels to canvas units, so drawing lands under the pointer at any
  // zoom and whatever the viewport crop.
  const toCanvas = useCallback((clientX: number, clientY: number): Point => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return [0, 0];
    return [(clientX - rect.left) / scale, (clientY - rect.top) / scale];
  }, [scale]);

  const onObjectPointerDown = useCallback(
    (e: React.PointerEvent, object: RoomObject, mode: "move" | "resize") => {
      // While the pen is out, clicking an object should draw over it rather
      // than pick it up.
      if (drawMode) return;
      // Backdrops are scenery, not furniture.
      if (object.kind === "backdrop") return;

      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      onSelect(object.id);

      dragRef.current = {
        id: object.id,
        mode,
        startX: e.clientX,
        startY: e.clientY,
        origin: { x: object.x, y: object.y, width: object.width, height: object.height },
      };
    },
    [drawMode, onSelect],
  );

  const onSurfacePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!drawMode) {
        // Pressing empty space begins a pan. Whether it was *also* a click to
        // deselect is decided on release, by how far the pointer travelled —
        // committing to one meaning here would cost the other.
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        panRef.current = { x: e.clientX, y: e.clientY, cx: centre.cx, cy: centre.cy };
        return;
      }
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      drawingRef.current = true;
      setStrokes((s) => [...s, [toCanvas(e.clientX, e.clientY)]]);
    },
    [drawMode, centre.cx, centre.cy, toCanvas],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const pan = panRef.current;
      if (pan) {
        // The room moves with the finger, so the centre moves against it.
        setCentre({
          cx: pan.cx - (e.clientX - pan.x) / scale,
          cy: pan.cy - (e.clientY - pan.y) / scale,
        });
        return;
      }

      if (drawingRef.current) {
        const point = toCanvas(e.clientX, e.clientY);
        setStrokes((s) => {
          if (s.length === 0) return s;
          const last = s[s.length - 1];
          const previous = last[last.length - 1];
          // Skip points closer than a couple of units: freehand input is far
          // denser than the line needs, and every extra point is payload
          // every visitor downloads.
          if (Math.hypot(point[0] - previous[0], point[1] - previous[1]) < 2) return s;
          return [...s.slice(0, -1), [...last, point]];
        });
        return;
      }

      const drag = dragRef.current;
      if (!drag) return;

      const dx = (e.clientX - drag.startX) / scale;
      const dy = (e.clientY - drag.startY) / scale;

      const patch =
        drag.mode === "move"
          ? { id: drag.id, x: drag.origin.x + dx, y: drag.origin.y + dy }
          : {
              id: drag.id,
              width: Math.max(60, drag.origin.width + dx),
              height: Math.max(60, drag.origin.height + dy),
            };

      const now = performance.now();
      if (now - lastSent.current > 40) lastSent.current = now;
      onMove(patch);
    },
    [onMove, scale, toCanvas],
  );

  const endInteraction = useCallback(
    (e: React.PointerEvent) => {
      const pan = panRef.current;
      if (pan) {
        panRef.current = null;
        // Under a few pixels of travel this was a tap, not a drag, and a tap on
        // empty space means "nothing is selected".
        if (Math.hypot(e.clientX - pan.x, e.clientY - pan.y) < 4) onSelect(null);
        return;
      }

      if (drawingRef.current) {
        drawingRef.current = false;
        return;
      }

      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;

      const dx = (e.clientX - drag.startX) / scale;
      const dy = (e.clientY - drag.startY) / scale;

      // One save, at the end. Everything before this was preview.
      onPlace(
        drag.mode === "move"
          ? { id: drag.id, x: drag.origin.x + dx, y: drag.origin.y + dy }
          : {
              id: drag.id,
              width: Math.max(60, drag.origin.width + dx),
              height: Math.max(60, drag.origin.height + dy),
            },
      );
    },
    [onPlace, onSelect, scale],
  );

  // Turns the pending scribble into a real object, positioned at its own
  // bounding box so it can afterwards be dragged like anything else.
  const commitDrawing = useCallback(() => {
    if (strokes.length === 0) return;

    const all = strokes.flat();
    const xs = all.map((p) => p[0]);
    const ys = all.map((p) => p[1]);
    const pad = drawWidth + 6;
    const minX = Math.min(...xs) - pad;
    const minY = Math.min(...ys) - pad;
    const maxX = Math.max(...xs) + pad;
    const maxY = Math.max(...ys) + pad;

    const relative = strokes.map((stroke) =>
      stroke.map(([x, y]) => [x - minX, y - minY] as Point),
    );

    onDrawn(relative, {
      x: minX,
      y: minY,
      w: Math.max(40, maxX - minX),
      h: Math.max(40, maxY - minY),
    });
    setStrokes([]);
  }, [drawWidth, onDrawn, strokes]);

  useEffect(() => {
    // Leaving draw mode commits whatever is pending rather than discarding it.
    if (!drawMode && strokes.length > 0) commitDrawing();
  }, [drawMode, strokes.length, commitDrawing]);

  const backdrop = objects.find((o) => o.kind === "backdrop");
  const placed = objects.filter((o) => o.kind !== "backdrop");

  // A photo backdrop is the one preset whose appearance is not in the
  // stylesheet: it is whatever image the room uploaded, covering the wall.
  const backdropPhoto =
    backdrop?.data?.preset === "photo" && backdrop.data?.upload_id
      ? uploadUrl(String(backdrop.data.upload_id))
      : null;

  return (
    <div className="canvas-viewport" ref={viewportRef}>
      <div
        className={`canvas backdrop-${backdrop?.data?.preset ?? "plain"}${drawMode ? " drawing" : ""}`}
        ref={canvasRef}
        style={{
          width: CANVAS_W,
          height: CANVAS_H,
          transform: `translate(${view.x}px, ${view.y}px) scale(${scale})`,
          ...(backdropPhoto
            ? {
                backgroundImage: `url("${backdropPhoto}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : null),
        }}
        onPointerDown={onSurfacePointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => {
          endInteraction(e);
          if (!drawingRef.current && strokes.length > 0) commitDrawing();
        }}
        onPointerCancel={endInteraction}
      >
        {placed.map((object) => {
          const tile = object.subject_user_id
            ? tilesByIdentity.get(
                `u_${object.subject_user_id}:${object.kind === "screen" ? "screen" : "cam"}`,
              )
            : undefined;

          // A screen frame only shows while that person is sharing. The row
          // persists so it returns to where the room put it.
          if (object.kind === "screen" && !tile) return null;

          return (
            <ObjectFrame
              key={object.id}
              object={object}
              tile={tile}
              name={
                object.subject_user_id ? nameByUserId.get(object.subject_user_id) ?? "Guest" : ""
              }
              isMine={object.subject_user_id === myUserId}
              selected={selectedId === object.id}
              interactive={!drawMode}
              onPointerDown={onObjectPointerDown}
            />
          );
        })}

        {/* The scribble in progress, before it becomes an object. */}
        {strokes.length > 0 && (
          <svg className="ink-layer" width={CANVAS_W} height={CANVAS_H}>
            {strokes.map((stroke, i) => (
              <path
                key={i}
                d={toPath(stroke)}
                stroke={STROKE_COLORS[drawColor] ?? STROKE_COLORS.chalk}
                strokeWidth={drawWidth}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
          </svg>
        )}
      </div>
    </div>
  );
}

export function toPath(stroke: Point[]): string {
  if (stroke.length === 0) return "";
  return stroke.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
}

function ObjectFrame({
  object,
  tile,
  name,
  isMine,
  selected,
  interactive,
  onPointerDown,
}: {
  object: RoomObject;
  tile?: Tile;
  name: string;
  isMine: boolean;
  selected: boolean;
  interactive: boolean;
  onPointerDown: (e: React.PointerEvent, o: RoomObject, mode: "move" | "resize") => void;
}) {
  return (
    <div
      className={`obj obj-${object.kind} shape-${object.shape}${selected ? " selected" : ""}`}
      style={{
        left: object.x,
        top: object.y,
        width: object.width,
        height: object.height,
        zIndex: object.z,
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        pointerEvents: interactive ? undefined : "none",
      }}
      onPointerDown={(e) => onPointerDown(e, object, "move")}
    >
      {object.kind === "camera" && <CameraBody tile={tile} name={name} isMine={isMine} />}
      {object.kind === "screen" && <ScreenBody tile={tile} name={name} />}
      {object.kind === "note" && <NoteBody object={object} />}
      {object.kind === "image" && <ImageBody object={object} />}
      {object.kind === "drawing" && <DrawingBody object={object} />}

      {selected && interactive && (
        <span
          className="resize-handle"
          onPointerDown={(e) => onPointerDown(e, object, "resize")}
        />
      )}
    </div>
  );
}

function CameraBody({ tile, name, isMine }: { tile?: Tile; name: string; isMine: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    const track = tile?.videoTrack;
    if (!el || !track) return;
    track.attach(el);
    return () => void track.detach(el);
  }, [tile?.videoTrack]);

  useEffect(() => {
    const el = audioRef.current;
    const track = tile?.audioTrack;
    if (!el || !track) return;
    track.attach(el);
    return () => void track.detach(el);
  }, [tile?.audioTrack]);

  return (
    <>
      {tile?.videoTrack ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isMine}
          // Your own preview is mirrored so moving right moves right.
          style={isMine ? { transform: "scaleX(-1)" } : undefined}
        />
      ) : (
        <div className="obj-empty">
          <span className="obj-initial">{(name || "?").slice(0, 1).toUpperCase()}</span>
        </div>
      )}
      {!isMine && tile?.audioTrack && <audio ref={audioRef} autoPlay />}
      <span className="obj-label">
        {tile?.micMuted && <span className="muted-pip">●</span>}
        {name}
        {isMine ? " (you)" : ""}
      </span>
    </>
  );
}

function ScreenBody({ tile, name }: { tile?: Tile; name: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    const track = tile?.videoTrack;
    if (!el || !track) return;
    track.attach(el);
    return () => void track.detach(el);
  }, [tile?.videoTrack]);

  // Never mirrored — text on a shared screen would read backwards.
  return (
    <>
      <video ref={videoRef} autoPlay playsInline className="screen-video" />
      <span className="obj-label">{name} — screen</span>
    </>
  );
}

function NoteBody({ object }: { object: RoomObject }) {
  // A text node, never markup. Note text is written by other people.
  return <div className={`note note-${object.data?.color ?? "yellow"}`}>{object.data?.text}</div>;
}

function ImageBody({ object }: { object: RoomObject }) {
  const uploadId = object.data?.upload_id;
  if (!uploadId) return <div className="obj-empty">No image</div>;

  return (
    <img
      src={uploadUrl(String(uploadId))}
      alt={object.data?.alt ?? ""}
      draggable={false}
    />
  );
}

function DrawingBody({ object }: { object: RoomObject }) {
  // Strokes come back as numbers and are turned into a path here. Nothing a
  // peer sent is ever interpreted as markup.
  const strokes = (object.data as any)?.strokes as Point[][] | undefined;
  if (!strokes?.length) return null;

  const color = STROKE_COLORS[(object.data as any)?.color] ?? STROKE_COLORS.chalk;
  const width = Number((object.data as any)?.width) || 3;

  // Drawn in the coordinates it was captured in, then scaled to whatever size
  // the object has been resized to.
  const all = strokes.flat();
  const w = Math.max(...all.map((p) => p[0]), 1);
  const h = Math.max(...all.map((p) => p[1]), 1);

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="drawing-svg">
      {strokes.map((stroke, i) => (
        <path
          key={i}
          d={toPath(stroke)}
          stroke={color}
          strokeWidth={width}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
